/**
 * Buses: Västtrafik's three designs, and a plain one in an operator's
 * colour. Built as `VehicleModel`s, as the trucks are, so they are baked for
 * traffic, parked and driven the same way.
 *
 * From Västtrafik's sketches ("Bussarna får nytt utseende"): all three are
 * their blue, the windows a black band, a block pattern of a lighter and a
 * darker blue on the lower sides rising toward the back, and the logo by
 * the front. They differ at the front:
 *
 *   - the area bus (områdesbuss, all other traffic), 12 m on two axles: blue
 *     all over;
 *   - the express (today's coloured express lines), 15 m on three axles, a
 *     tag axle behind the drive axle: its front quarter pink, EXPRESS in
 *     yellow on its side;
 *   - the metro (today's trunk lines, stombussar): its front green, sweeping
 *     back along its sides.
 *
 * The plain bus has the area bus's shape and none of Västtrafik's marks.
 * The body's blue is the paint, tinted per bus in traffic, so the plain one
 * takes any colour; the patterns and fronts are their own colours.
 *
 * Positions are metres in the vehicle's frame: x right, y up from the ground,
 * z forward-negative, the origin at the middle of the body, so its box is
 * even about it, a little behind the middle of its wheelbase. The doors
 * are on the right, the kerb's side.
 */

import { BoxGeometry, Group, Mesh, SpotLight, TorusGeometry } from 'three';
import type { MeshStandardMaterial } from 'three';
import type { BusKind, VehicleModel, VehicleWheel } from './Vehicle.js';
import {
  buildWheel, chrome, clusterGeometry, clusterMaterial, dash, faceted, glass, headlamp, indicator, inside, part, plate,
  taillamp, trim, upholstery,
} from './Vehicle.js';

/** Each design's dimensions, metres: its ends, its axles, its doors' middles. */
export const BUSES: Record<BusKind, { front: number; back: number; axles: number[]; doors: number[] }> = {
  busArea: { front: -6.0, back: 6.0, axles: [-3.15, 2.75], doors: [-5.2, 0.15] },
  busMetro: { front: -6.0, back: 6.0, axles: [-3.15, 2.75], doors: [-5.2, 0.15, 4.3] },
  busExpress: { front: -7.4, back: 7.4, axles: [-3.95, 2.95, 4.35], doors: [-6.6, 0.4] },
  busCity: { front: -6.0, back: 6.0, axles: [-3.15, 2.75], doors: [-5.2, 0.15] },
};
const WIDTH = 2.55;
const FLOOR = 0.34;
const BELT = 1.08;
const EAVE = 2.62;
const ROOF = 3.02;
const WHEEL = 0.5;
/** Västtrafik's colours: the blue is the paint, these are the rest. */
export const VT_BLUE = 0x1a9bd7;
/**
 * The livery's materials, made on first use: `Vehicle.ts` imports this
 * module, and this one its materials, so nothing here may call into it
 * while the modules load.
 */
let livery: Record<'lightBlue' | 'darkBlue' | 'pink' | 'green' | 'yellow' | 'white' | 'band', MeshStandardMaterial> | undefined;
function colours() {
  livery ??= {
    lightBlue: faceted(0x6fcdf1, 0.45, 0.1),
    darkBlue: faceted(0x0e6fae, 0.45, 0.1),
    pink: faceted(0xf2a7c6, 0.45, 0.1),
    green: faceted(0x63c3a2, 0.45, 0.1),
    yellow: faceted(0xf6d33c, 0.4, 0.1),
    white: faceted(0xf5f6f4, 0.5),
    band: faceted(0x15181c, 0.25, 0.4),
  };
  return livery;
}

const box = (w: number, h: number, d: number, material: MeshStandardMaterial, x: number, y: number, z: number) =>
  part(new BoxGeometry(w, h, d), material, x, y, z);

/**
 * The block pattern along one side, `side` -1 left, 1 right: squares of the
 * two blues stepping up toward the back in runs, as on the sketches.
 */
function blocks(body: Group, side: -1 | 1, from: number, to: number): void {
  const { lightBlue, darkBlue } = colours();
  const s = 0.42, x = side * (WIDTH / 2 + 0.012);
  const runs = Math.max(1, Math.floor((to - from) / 3.4));
  for (let r = 0; r < runs; r++) {
    const z0 = from + r * ((to - from) / runs);
    for (let k = 0; k < 6; k++) {
      // Up a block every second one, toward the back, each a little apart.
      const y = FLOOR + 0.12 + s / 2 + Math.floor(k / 2) * s * 0.85;
      if (y + s / 2 > BELT - 0.02) break;
      const z = z0 + k * s * 0.95;
      body.add(box(0.02, s * 0.9, s * 0.9, k % 2 === 0 ? lightBlue : darkBlue, x, y, z));
    }
  }
}

/** A door: dark glass in a frame, flush on the right side. */
function door(body: Group, z: number, wide: boolean): void {
  const w = wide ? 1.25 : 0.95, x = WIDTH / 2 + 0.015;
  body.add(box(0.03, EAVE - FLOOR - 0.15, w, trim, x, (EAVE + FLOOR) / 2 - 0.05, z));
  body.add(box(0.03, EAVE - FLOOR - 0.35, w / 2 - 0.08, glass, x + 0.01, (EAVE + FLOOR) / 2, z - w / 4));
  body.add(box(0.03, EAVE - FLOOR - 0.35, w / 2 - 0.08, glass, x + 0.01, (EAVE + FLOOR) / 2, z + w / 4));
}

/** Two wheels on an axle at `z`; the front ones steer, the rest are twins. */
function axle(root: Group, z: number, front: boolean, into: VehicleWheel[]): void {
  const x = WIDTH / 2 - 0.22;
  for (const side of [-1, 1] as const) {
    const pivot = new Group();
    pivot.position.set(side * x, WHEEL, z);
    const spin = buildWheel(side, WHEEL);
    if (!front) spin.scale.x = 1.7;
    pivot.add(spin);
    root.add(pivot);
    into.push({ pivot, spin, front, x: side * x, z });
  }
}

/**
 * Build a bus of a kind. `colour` is its paint: Västtrafik's blue for their
 * designs, any for the plain one.
 */
export function createBus(kind: BusKind, colour = VT_BLUE, _open = true): VehicleModel {
  const spec = BUSES[kind];
  const { pink, green, yellow, white, band } = colours();
  const root = new Group();
  const body = new Group();
  root.add(body);
  const paint = faceted(colour, 0.4, 0.15);
  const { front, back } = spec;
  const length = back - front, mid = (front + back) / 2, half = WIDTH / 2;
  const vt = kind !== 'busCity';

  // The shell: the lower body, the window band, the roof and its pod.
  body.add(box(WIDTH, BELT - FLOOR, length, paint, 0, (BELT + FLOOR) / 2, mid));
  body.add(box(WIDTH - 0.04, EAVE - BELT, length - 0.06, band, 0, (EAVE + BELT) / 2, mid));
  body.add(box(WIDTH, ROOF - EAVE, length, paint, 0, (ROOF + EAVE) / 2, mid));
  body.add(box(1.7, 0.28, 3.2, paint, 0, ROOF + 0.14, back - 3.0));
  // Side windows: panes in the band, pillars between, both sides.
  for (const side of [-1, 1] as const) {
    for (let z = front + 2.2; z < back - 0.8; z += 1.55) {
      if (side === 1 && spec.doors.some(d => Math.abs(d - z) < 0.9)) continue;
      body.add(box(0.02, EAVE - BELT - 0.22, 1.35, glass, side * (half + 0.004), (EAVE + BELT) / 2 + 0.04, z));
    }
    // Wheel arches: dark over each axle.
    for (const z of spec.axles) body.add(box(0.03, 0.2, WHEEL * 2.5, trim, side * (half + 0.006), WHEEL * 2 + 0.12, z));
    // The skirt along the bottom.
    body.add(box(0.03, 0.1, length - 0.2, trim, side * (half + 0.008), FLOOR + 0.05, mid));
  }
  for (const [i, z] of spec.doors.entries()) door(body, z, i > 0);

  // The front: the deep windscreen, the sign over it, lamps, bumper, mirrors.
  body.add(box(WIDTH - 0.16, EAVE - 0.95, 0.04, glass, 0, (EAVE + 0.95) / 2 - 0.05, front - 0.01));
  body.add(box(WIDTH - 0.3, 0.32, 0.05, band, 0, EAVE + 0.18, front - 0.02));
  body.add(box(WIDTH + 0.02, 0.32, 0.12, trim, 0, 0.5, front + 0.02));
  for (const side of [-1, 1] as const) {
    body.add(box(0.36, 0.16, 0.05, headlamp, side * 0.9, 0.82, front - 0.03));
    body.add(box(0.14, 0.1, 0.05, indicator, side * 1.12, 0.66, front - 0.03));
    // Mirrors on long arms, out and down ahead of the glass.
    body.add(box(0.05, 0.05, 0.55, trim, side * (half + 0.15), 2.55, front + 0.05));
    body.add(box(0.08, 0.48, 0.2, trim, side * (half + 0.4), 2.3, front - 0.2));
  }
  body.add(box(0.52, 0.11, 0.02, plate, 0, 0.5, front - 0.06));
  // The back: a small window, the engine's grille, the lamps.
  body.add(box(WIDTH - 0.5, 0.7, 0.03, glass, 0, 2.05, back + 0.01));
  body.add(box(WIDTH - 0.4, 0.45, 0.03, trim, 0, 0.75, back + 0.01));
  for (const side of [-1, 1] as const) {
    body.add(box(0.2, 0.55, 0.05, taillamp, side * (half - 0.15), 1.25, back + 0.02));
    body.add(box(0.16, 0.14, 0.05, indicator, side * (half - 0.15), 1.6, back + 0.02));
  }
  body.add(box(0.52, 0.11, 0.02, plate, 0, 0.62, back + 0.025));

  if (vt) {
    // The block pattern on both sides, behind the front door to the tail.
    for (const side of [-1, 1] as const) blocks(body, side, front + 2.0, back - 0.6);
    // The logo, white, low by the front on each side.
    for (const side of [-1, 1] as const) body.add(box(0.02, 0.13, 0.95, white, side * (half + 0.014), FLOOR + 0.42, front + 1.25));
  }
  if (kind === 'busExpress') {
    // Its front quarter pink, round the nose and back along both sides, EXPRESS in yellow.
    const pz = front + 1.9;
    body.add(box(WIDTH + 0.01, BELT - FLOOR, 3.8, pink, 0, (BELT + FLOOR) / 2, pz));
    body.add(box(WIDTH + 0.01, ROOF - EAVE, 3.8, pink, 0, (ROOF + EAVE) / 2, pz));
    body.add(box(WIDTH - 0.2, 0.3, 0.03, pink, 0, 0.8, front - 0.025));
    for (const side of [-1, 1] as const) body.add(box(0.02, 0.17, 1.15, yellow, side * (half + 0.016), BELT - 0.25, front + 2.7));
  }
  if (kind === 'busMetro') {
    // Green over the nose, and in a sweep back along its sides below the windows.
    body.add(box(WIDTH - 0.2, 0.45, 0.03, green, 0, 0.82, front - 0.025));
    for (const side of [-1, 1] as const) {
      for (let k = 0; k < 4; k++) {
        const z = front + 0.4 + k * 0.6, h = (BELT - FLOOR) * (1 - k * 0.22);
        body.add(box(0.02, h, 0.6, green, side * (half + 0.015), FLOOR + h / 2, z));
      }
    }
  }

  const wheels: VehicleWheel[] = [];
  spec.axles.forEach((z, i) => axle(root, z, i === 0, wheels));

  // Inside: the driver's place, front left, high over the front axle.
  const seatX = -0.62, hipY = 1.25, hipZ = front + 1.45;
  const wheelY = 1.6, wheelZ = front + 0.85;
  body.add(inside(new BoxGeometry(1.1, 0.3, 0.5), dash, seatX, 1.0, front + 0.45));
  body.add(inside(new BoxGeometry(0.55, 0.14, 0.55), upholstery, seatX, hipY - 0.1, hipZ));
  const seatBack = inside(new BoxGeometry(0.52, 0.7, 0.12), upholstery, seatX, hipY + 0.3, hipZ + 0.3);
  seatBack.rotation.x = 0.15;
  body.add(seatBack);
  const steering = inside(new TorusGeometry(0.25, 0.03, 5, 14), dash, seatX, wheelY, wheelZ);
  steering.rotation.x = -1.1;
  body.add(steering);
  const cluster = new Mesh(clusterGeometry, clusterMaterial);
  cluster.position.set(seatX, wheelY + 0.1, wheelZ - 0.22);
  cluster.rotation.x = -0.35;
  cluster.userData.interior = true;
  cluster.visible = false;
  body.add(cluster);
  // A bus's driver gets in by the front door; the "door" here is its leaf.
  const doorLeaf = new Group();
  doorLeaf.visible = false;
  doorLeaf.position.set(half + 0.02, 0, spec.doors[0] - 0.5);
  doorLeaf.add(part(new BoxGeometry(0.04, EAVE - FLOOR - 0.2, 0.95), chrome, 0, (EAVE + FLOOR) / 2, 0.47));
  body.add(doorLeaf);

  const headlights = ([-1, 1] as const).map((side) => {
    const beam = new SpotLight(0xfff0d0, 0, 70, 0.42, 0.6, 1.4);
    beam.position.set(side * 0.9, 0.82, front - 0.06);
    beam.target.position.set(side * 0.9 + side * 1.6, 0, front - 24);
    body.add(beam, beam.target);
    return beam;
  }) as [SpotLight, SpotLight];

  return {
    kind, root, body, wheels, wheelRadius: WHEEL, headlights, paint, door: doorLeaf, cluster,
    seat: { hipY, hipZ, wheelY, wheelZ, pedalY: 0.55, pedalZ: front + 0.55, x: seatX, outsideX: half + 0.8 },
  };
}
